import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, CardBody, Eyebrow, FormField, Input, PageHeader } from '@/shared/components';
import { useApi, toApiError, type InvoiceTemplateResponse } from '@/shared/api';
import {
  initialInvoiceTemplateForm,
  toCreateRequest,
  toUpdateRequest,
  type InvoiceTemplateForm,
} from './invoiceTemplateForm';
import { InvoiceTemplateLogoSection } from './InvoiceTemplateLogoSection';
import { InvoiceTemplateSenderFields } from './InvoiceTemplateSenderFields';

interface Props {
  /** null = neue Vorlage, sonst Bearbeiten. */
  template: InvoiceTemplateResponse | null;
  onCancel: () => void;
  onSaved: () => void;
}

export function InvoiceTemplateEditor({ template, onCancel, onSaved }: Props) {
  const api = useApi();
  const isEdit = template !== null;
  const [form, setForm] = useState(() => initialInvoiceTemplateForm(template));
  const set = (patch: Partial<InvoiceTemplateForm>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () =>
      isEdit && template
        ? api.invoiceTemplates.update(template.id, toUpdateRequest(form))
        : api.invoiceTemplates.create(toCreateRequest(form)),
    onSuccess: () => {
      toast.success(isEdit ? 'Rechnungsvorlage gespeichert' : 'Rechnungsvorlage angelegt');
      onSaved();
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      {/* Vorher führte aus dem Editor nur „Abbrechen" ganz unten zurück — nach dem Scrollen
          durch vierzehn Felder war der Rückweg ausser Sicht. */}
      <Button variant="ghost" size="sm" type="button" className="mb-2" onClick={onCancel}>
        Zurück zur Übersicht
      </Button>
      <PageHeader
        eyebrow="Verwaltung"
        title={isEdit ? 'Vorlage' : 'Neue Vorlage'}
        titleAccent={isEdit ? 'bearbeiten' : 'anlegen'}
        description={
          isEdit
            ? `Absenderdaten und Bankverbindung von „${template?.name}".`
            : 'Absenderdaten und Bankverbindung einer weiteren Rechnungsvorlage.'
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <InvoiceTemplateSenderFields form={form} isEdit={isEdit} set={set} />

        <Card>
          <CardBody className="p-5">
            <Eyebrow variant="muted">Bankverbindung</Eyebrow>
            <div className="mt-3 space-y-4">
              <FormField label="IBAN">
                <Input value={form.iban} onChange={(e) => set({ iban: e.target.value })} />
              </FormField>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="BIC">
                  <Input value={form.bic} onChange={(e) => set({ bic: e.target.value })} />
                </FormField>
                <FormField label="Bank">
                  <Input
                    value={form.bankName}
                    onChange={(e) => set({ bankName: e.target.value })}
                  />
                </FormField>
              </div>
            </div>

            <Eyebrow variant="muted" className="mt-6">
              Zahlungsziel
            </Eyebrow>
            <div className="mt-3">
              <FormField
                label="Tage bis zur Fälligkeit"
                hint="Belegt die Fälligkeit manueller Rechnungen vor, dort überschreibbar. Leer lassen heißt: der Aussteller entscheidet je Rechnung. Rechnungen über Booking.com oder Airbnb tragen nie eine Fälligkeit — dort hat der Kanal bereits kassiert."
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={form.paymentTermDays}
                  onChange={(e) => set({ paymentTermDays: e.target.value })}
                />
              </FormField>
            </div>

            {isEdit && template && <InvoiceTemplateLogoSection template={template} />}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button variant="brand" type="submit" loading={save.isPending} disabled={!form.name.trim()}>
          {isEdit ? 'Speichern' : 'Anlegen'}
        </Button>
      </div>
    </form>
  );
}
